import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// Prevent unhandled errors from crashing iOS WebKit content process
window.addEventListener('error', (e) => {
  console.error('Global error:', e.message);
});
window.addEventListener('unhandledrejection', (e) => {
  console.error('Unhandled rejection:', e.reason);
  e.preventDefault();
});

// Service Worker: register the share-target SW so Android's "Share to
// PeopleiKnow" flow can POST an image into the PWA. Any OTHER service
// workers registered by earlier app versions are unregistered here —
// leaving stale SWs live is a common source of "login is broken on my
// phone" after a deploy (they serve the old cached bundle even after
// refresh). Idempotent + safe to leave in place.
const SHARE_TARGET_SW_PATH = '/share-target-sw.js';
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations()
    .then((regs) => regs.forEach((r) => {
      const url = r.active?.scriptURL || r.installing?.scriptURL || r.waiting?.scriptURL || '';
      if (!url.endsWith(SHARE_TARGET_SW_PATH)) {
        r.unregister().catch(() => {});
      }
    }))
    .catch(() => {});
  // Register (or refresh) the share-target SW after page load so it
  // doesn't slow first paint. The SW's `install → skipWaiting +
  // claim` combo means it takes over immediately without a reload.
  window.addEventListener('load', () => {
    navigator.serviceWorker.register(SHARE_TARGET_SW_PATH, { scope: '/' })
      .catch(() => {/* SW registration is optional; PWA still works without it */});
  });
}

// Detect iOS (Safari and Chrome both use WebKit on iOS)
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

// Belt-and-suspenders: force-stop PostHog session recording on iOS
// PostHog's session recording + Cloudflare iframes can crash iOS WebKit
if (isIOS && window.posthog) {
  try {
    window.posthog.stopSessionRecording?.();
  } catch (e) { /* ignore */ }
}

// Remove platform injected badges that cover navigation (safe for iOS).
// IMPORTANT: must NOT hide Radix portals (Dialog/Popover/Sonner) which also
// mount as direct children of <body>. We target only the actual Emergent badge.
const BADGE_SELECTORS = [
  '[data-testid="emergent-badge"]',
  '[id*="emergent-badge"]',
  '[class*="emergent-badge"]',
  'body > a[href*="emergent.sh"]',
  'body > a[href*="emergent.app"]',
];
const PORTAL_MARKERS = [
  '[role="dialog"]',
  '[role="alertdialog"]',
  '[data-radix-portal]',
  '[data-radix-popper-content-wrapper]',
  '[data-sonner-toaster]',
];
const removeBadge = () => {
  try {
    document.querySelectorAll(BADGE_SELECTORS.join(',')).forEach(el => {
      // Defensive: if it happens to contain a portal element, don't hide it
      if (PORTAL_MARKERS.some(sel => el.matches?.(sel) || el.querySelector?.(sel))) return;
      el.style.setProperty('display', 'none', 'important');
    });
  } catch (e) { /* ignore */ }
};
let badgeRunCount = 0;
const observer = new MutationObserver(() => {
  badgeRunCount++;
  removeBadge();
  // Stop observing after 10 runs to prevent loops on iOS
  if (badgeRunCount > 10) observer.disconnect();
});
observer.observe(document.body, { childList: true, subtree: false });
setTimeout(removeBadge, 1000);
setTimeout(removeBadge, 3000);
// Disconnect observer after 10 seconds regardless
setTimeout(() => observer.disconnect(), 10000);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
